"use client";

import { cn } from "@/lib/utils";
import { type ComponentProps, memo, useMemo } from "react";
import { Streamdown } from "streamdown";
import { preprocessCitations } from "@/utils/citations";

type ResponseProps = ComponentProps<typeof Streamdown>;

export const Response = memo(
  ({ className, children, ...props }: ResponseProps) => {
    // RFC-070: Pre-process citations [^N] → [[^N]](#cite-N) for Streamdown
    const processedContent = useMemo(() => {
      if (typeof children === 'string') {
        const processed = preprocessCitations(children);
        // Debug logging
        if (process.env.NODE_ENV === 'development' && children !== processed) {
          const hasCitations = /\[\^(\d+)\]/g.test(children);
          console.log('[Response] Preprocessed citations:', {
            hasCitations,
            citationCount: (children.match(/\[\^(\d+)\]/g) || []).length,
            original: children,
            processed: processed,
            sample: processed.substring(processed.indexOf('[') - 20, processed.indexOf('[') + 50),
          });
        }
        return processed;
      }
      return children;
    }, [children]);

    return (
      <Streamdown
        className={cn(
          "screenata-response size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
          className
        )}
        {...props}
      >
        {processedContent}
      </Streamdown>
    );
  },
  (prevProps, nextProps) => prevProps.children === nextProps.children
);

Response.displayName = "Response";
